import React, { useEffect, useRef, useState } from 'react';

import { IconButton, Spinner } from '@infoods/lifebase-ui';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import classes from './LeadConnectorChat.module.scss';

const WIDGET_LOADED_EVENT = 'LC_chatWidgetLoaded';
// Production LeadConnector widget, used in all environments
const WIDGET_ID = '6a4e6aab88718db6dd2f20b4';

const createLoaderScript = (): HTMLScriptElement => {
  const script = document.createElement('script');
  script.src = 'https://widgets.leadconnectorhq.com/loader.js';
  script.dataset.resourcesUrl =
    'https://widgets.leadconnectorhq.com/chat-widget/loader.js';
  script.dataset.widgetId = WIDGET_ID;

  return script;
};

/**
 * LeadConnector (GoHighLevel) AI help bot, rendered inside the Help & Support panel.
 *
 * The loader mounts <chat-widget> into its own script tag's parentNode, so the script must be
 * appended to the host div - appending it to <body> is the old floating-bubble behaviour.
 * A script element executes once, so every mount needs a FRESH element: re-appending an existing
 * one never re-runs the loader and would leave the new host empty.
 * Once loader.js is fetched there is no callback and no failure signal: readiness arrives only
 * as the window event LC_chatWidgetLoaded, and an internal widget failure is silent.
 */
const LeadConnectorChat = ({ onClose }: { onClose: () => void }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    const handleWidgetLoaded = () => setIsLoading(false);
    const handleLoaderError = () => {
      setIsLoading(false);
      setIsError(true);
    };

    const script = createLoaderScript();
    script.onerror = handleLoaderError;

    window.addEventListener(WIDGET_LOADED_EVENT, handleWidgetLoaded);
    host.appendChild(script);

    return () => {
      window.removeEventListener(WIDGET_LOADED_EVENT, handleWidgetLoaded);
      script.onerror = null;
      script.remove();
    };
  }, []);

  return (
    <div className={classes.chatHostContainer}>
      {isError ? (
        <div>The help chat could not be loaded.</div>
      ) : (
        <div ref={hostRef} className={classes.chatHost} />
      )}
      {!isLoading && (
        <IconButton
          aria-label="Close help center"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseRoundedIcon />
        </IconButton>
      )}
      <Spinner isLoading={isLoading} />
    </div>
  );
};

export { LeadConnectorChat };
